import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Database, Pencil, Info, X } from 'lucide-react';
import usePopoverAnchor from './usePopoverAnchor';

/**
 * Definitions for each categorical baseline value. The select dropdowns are
 * tiny labels ("Mid", "Flat", "Moderate") that don't tell the user what
 * thresholds they correspond to. Click ⓘ next to a field to see the full
 * meaning of every option.
 */
const FIELD_DEFINITIONS = {
    vacancy_band: {
        title: 'Vacancy Band',
        body: 'Local rental vacancy rate, used to gauge market absorption.',
        options: [
            { label: 'Low',  range: '< 5%',   meaning: 'Tight market — full occupancy, landlord-favorable conditions.' },
            { label: 'Mid',  range: '5–10%',  meaning: 'Typical equilibrium — most metros sit here.' },
            { label: 'High', range: '> 10%',  meaning: 'Supply exceeds demand — NOI pressure, lease-up risk.' },
        ],
    },
    rent_trend: {
        title: 'Rent Trend',
        body: 'Direction of asking rents over the past 12 months in this submarket.',
        options: [
            { label: 'Rising',  range: '> +2% YoY',     meaning: 'Positive momentum, but affordability tightens.' },
            { label: 'Flat',    range: '±2% YoY',       meaning: 'Steady — rents tracking inflation roughly.' },
            { label: 'Falling', range: '< −2% YoY',     meaning: 'Asset relevance declining; rent compression on renewals.' },
        ],
    },
    oversight_strength: {
        title: 'Oversight Strength',
        body: 'Intensity of state HFA monitoring for the property\'s regulatory program.',
        options: [
            { label: 'Strong',   range: 'Tier-1 HFA',    meaning: 'Regular MOR cycles, fast 8823 follow-up, audited compliance (e.g., NY, MN, CA, MA HFAs).' },
            { label: 'Moderate', range: 'Standard',      meaning: 'Routine annual monitoring; typical 8823 turnaround.' },
            { label: 'Weak',     range: 'Minimal',       meaning: 'Inconsistent monitoring; longer cycles between reviews.' },
        ],
    },
};

/**
 * Editable summary of what the proxy fetch returned. Stale Census data,
 * regional knowledge the public sources don't have, end-of-period rent
 * spikes — all real reasons the user might want to override the figures.
 *
 * Each field is an inline input. Edits flow back via `onChange(patch)`.
 * The proxy ratings (Market Stress, Rent-to-AMI, Oversight) re-derive
 * upstream when these change.
 */
export default function BaselineSummary({ snapshot, onChange }) {
    if (!snapshot) return null;

    const update = (patch) => onChange?.({ ...snapshot, ...patch });

    return (
        <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-4">
            <div className="flex items-center gap-2 mb-3">
                <Database size={14} className="text-blue-600 dark:text-blue-400" />
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 tracking-tight">
                    Baseline figures
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 ml-1 inline-flex items-center gap-1">
                    <Pencil size={11} /> editable — correct anything stale
                </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Vacancy Band" definition={FIELD_DEFINITIONS.vacancy_band}>
                    <select value={snapshot.vacancy_band || ''}
                            onChange={(e) => update({ vacancy_band: e.target.value || null })}
                            className={selectCls}>
                        <option value="">—</option>
                        <option value="Low">Low</option>
                        <option value="Mid">Mid</option>
                        <option value="High">High</option>
                    </select>
                </Field>
                <Field label="Rent Trend" definition={FIELD_DEFINITIONS.rent_trend}>
                    <select value={snapshot.rent_trend || ''}
                            onChange={(e) => update({ rent_trend: e.target.value || null })}
                            className={selectCls}>
                        <option value="">—</option>
                        <option value="Rising">Rising</option>
                        <option value="Flat">Flat</option>
                        <option value="Falling">Falling</option>
                    </select>
                </Field>
                <Field label="Oversight" definition={FIELD_DEFINITIONS.oversight_strength}>
                    <select value={snapshot.oversight_strength || ''}
                            onChange={(e) => update({ oversight_strength: e.target.value || null })}
                            className={selectCls}>
                        <option value="">—</option>
                        <option value="Strong">Strong</option>
                        <option value="Moderate">Moderate</option>
                        <option value="Weak">Weak</option>
                    </select>
                </Field>

                <Field label="Area Median Income" prefix="$">
                    <input type="number" min={0} step={500}
                           value={snapshot.area_median_income ?? ''}
                           onChange={(e) => update({ area_median_income: e.target.value === '' ? null : Number(e.target.value) })}
                           className={inputCls} />
                </Field>
                <Field label="Typical 1-2BR Rent" prefix="$">
                    <input type="number" min={0} step={25}
                           value={snapshot.typical_rent ?? ''}
                           onChange={(e) => update({ typical_rent: e.target.value === '' ? null : Number(e.target.value) })}
                           className={inputCls} />
                </Field>
                <Field label="Rent / AMI" suffix="%">
                    <input type="number" min={0} max={100} step={0.5}
                           value={snapshot.rent_to_AMI != null ? Number((snapshot.rent_to_AMI * 100).toFixed(1)) : ''}
                           onChange={(e) => update({ rent_to_AMI: e.target.value === '' ? null : Number(e.target.value) / 100 })}
                           className={inputCls} />
                </Field>
            </div>

            {snapshot.data_sources && (
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                    Source: {snapshot.data_sources}
                </div>
            )}
        </section>
    );
}

const selectCls = 'w-full px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:outline-none';
const inputCls  = 'w-full px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-slate-100 tabular-nums focus:ring-2 focus:ring-blue-500 focus:outline-none';

function Field({ label, tip, definition, prefix, suffix, children }) {
    const [showDef, setShowDef] = useState(false);
    const triggerRef = useRef(null);
    return (
        <label className="block relative">
            <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                    {label}
                    {definition && (
                        <button type="button" ref={triggerRef}
                                onClick={(e) => { e.preventDefault(); setShowDef(v => !v); }}
                                className="p-0.5 -m-0.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                title="What does each option mean?">
                            <Info size={11} />
                        </button>
                    )}
                </span>
                {tip && <span className="text-[10px] text-slate-400 dark:text-slate-500">{tip}</span>}
            </div>
            <div className="relative">
                {prefix && <span className="absolute left-2.5 top-1.5 text-sm text-slate-500 dark:text-slate-400 pointer-events-none">{prefix}</span>}
                {React.cloneElement(children, {
                    className: `${children.props.className} ${prefix ? 'pl-6' : ''} ${suffix ? 'pr-7' : ''}`,
                })}
                {suffix && <span className="absolute right-2.5 top-1.5 text-sm text-slate-500 dark:text-slate-400 pointer-events-none">{suffix}</span>}
            </div>
            {definition && showDef && (
                <FieldDefinitionPopover definition={definition} triggerRef={triggerRef} onClose={() => setShowDef(false)} />
            )}
        </label>
    );
}

function FieldDefinitionPopover({ definition, triggerRef, onClose }) {
    const popoverRef = useRef(null);
    const positionStyle = usePopoverAnchor(triggerRef, true, { preferredWidth: 288, placement: 'bottom-left' });
    useEffect(() => {
        const onClick = (e) => {
            if (popoverRef.current?.contains(e.target)) return;
            if (triggerRef?.current?.contains(e.target)) return;
            onClose?.();
        };
        const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onEsc);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onEsc);
        };
    }, [onClose, triggerRef]);

    return createPortal(
        <div ref={popoverRef}
             style={positionStyle}
             className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg ring-1 ring-black/5">
            <div className="flex items-start justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800">
                <div>
                    <div className="text-xs font-semibold text-slate-900 dark:text-white tracking-tight">{definition.title}</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{definition.body}</div>
                </div>
                <button onClick={onClose} className="p-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
                    <X size={12} />
                </button>
            </div>
            <ul className="px-3 py-2 space-y-2">
                {definition.options.map(o => (
                    <li key={o.label} className="text-[11px] leading-relaxed">
                        <div className="flex items-baseline gap-2">
                            <span className="font-semibold text-slate-800 dark:text-slate-100">{o.label}</span>
                            <span className="text-slate-500 dark:text-slate-400 tabular-nums">{o.range}</span>
                        </div>
                        <div className="text-slate-600 dark:text-slate-300 mt-0.5">{o.meaning}</div>
                    </li>
                ))}
            </ul>
        </div>,
        document.body
    );
}
